/**
 * App-lock with biometric (Face ID / Touch ID / fingerprint).
 *
 * Scope (V1):
 *   - Opt-in toggle in /account.
 *   - When enabled, on every COLD app start the user is greeted with a
 *     biometric prompt before any signed-in content renders.
 *   - Background → foreground resume is NOT a lock event (deliberate; ops
 *     managers shouldn't have to re-authenticate every time they glance at
 *     the app between tasks). If the app is force-killed and reopened,
 *     that's a cold start and the lock applies.
 *
 * Web:
 *   - All functions become no-ops. Capacitor.isNativePlatform() guards
 *     everything; on web isAvailable() returns false and isEnabled()
 *     returns false, so the lock guard never renders.
 *
 * Storage:
 *   - The "enabled" preference is kept in Capacitor Preferences (UserDefaults
 *     on iOS, SharedPreferences on Android). Per-user — keyed by profile_id.
 *
 * Privacy:
 *   - We never receive the biometric template. iOS/Android only hand us a
 *     yes/no signal from their secure enclave. Documented in the privacy
 *     policy mobile additions.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  BiometricAuth,
  BiometryError,
  BiometryErrorType,
  type CheckBiometryResult,
} from '@aparajita/capacitor-biometric-auth';
import { captureError, addBreadcrumb } from '@/lib/error-monitor';

const ENABLED_PREF_KEY_PREFIX = 'biometric_lock_enabled:'; // suffix: profile_id

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

function enabledKey(profileId: string): string {
  return `${ENABLED_PREF_KEY_PREFIX}${profileId}`;
}

/**
 * Whether the current device supports biometric auth at all. Returns
 * false on web and on devices where biometry is hardware-unavailable
 * or has not been enrolled.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const info: CheckBiometryResult = await BiometricAuth.checkBiometry();
    return info.isAvailable;
  } catch (err) {
    captureError(err, { context: 'biometric.isBiometricAvailable' });
    return false;
  }
}

/**
 * Returns a human-readable label for the device's biometry type. Useful
 * for showing "Use Face ID" vs "Use Touch ID" in UI copy.
 */
export async function biometryLabel(): Promise<string> {
  if (!isNative()) return 'Biometric';
  try {
    const info = await BiometricAuth.checkBiometry();
    // biometryType is an enum: 0=none, 1=touchId, 2=faceId, 3=fingerprint,
    // 4=faceAuthentication, 5=irisAuthentication
    switch (info.biometryType) {
      case 1: return 'Touch ID';
      case 2: return 'Face ID';
      case 3: return 'Fingerprint';
      case 4: return 'Face Unlock';
      case 5: return 'Iris Unlock';
      default: return 'Biometric';
    }
  } catch {
    return 'Biometric';
  }
}

export async function isLockEnabled(profileId: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { value } = await Preferences.get({ key: enabledKey(profileId) });
    return value === 'true';
  } catch (err) {
    captureError(err, { context: 'biometric.isLockEnabled', profileId });
    return false;
  }
}

export async function setLockEnabled(profileId: string, enabled: boolean): Promise<void> {
  if (!isNative()) return;
  try {
    await Preferences.set({ key: enabledKey(profileId), value: enabled ? 'true' : 'false' });
    addBreadcrumb('biometric.setLockEnabled', 'auth', { profileId, enabled });
  } catch (err) {
    captureError(err, { context: 'biometric.setLockEnabled', profileId, enabled });
    throw err;
  }
}

/**
 * Prompt the user to authenticate with biometry. Returns true on success,
 * false on user cancel or hardware failure. Throws only on programming
 * errors (e.g. plugin not loaded).
 */
export async function promptBiometric(reason: string): Promise<boolean> {
  if (!isNative()) return true; // web bypass — no lock on web
  try {
    await BiometricAuth.authenticate({
      reason,
      // iOS-specific
      cancelTitle: 'Sign out',
      iosFallbackTitle: 'Enter passcode',
      // Android-specific
      androidTitle: 'DashAlign',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
      // Allow device PIN/passcode as a fallback so a user with a partial
      // glove (or sweaty hands) doesn't get locked out.
      allowDeviceCredential: true,
    });
    addBreadcrumb('biometric.promptBiometric success', 'auth');
    return true;
  } catch (err) {
    if (err instanceof BiometryError) {
      addBreadcrumb('biometric.promptBiometric denied', 'auth', {
        code: err.code,
        message: err.message,
      });
      // User-cancel / not-enrolled / etc. — return false, don't throw.
      if (
        err.code === BiometryErrorType.userCancel ||
        err.code === BiometryErrorType.userFallback ||
        err.code === BiometryErrorType.appCancel ||
        err.code === BiometryErrorType.systemCancel ||
        err.code === BiometryErrorType.authenticationFailed ||
        err.code === BiometryErrorType.biometryNotAvailable ||
        err.code === BiometryErrorType.biometryNotEnrolled ||
        err.code === BiometryErrorType.biometryLockout
      ) {
        return false;
      }
    }
    captureError(err, { context: 'biometric.promptBiometric' });
    return false;
  }
}
